import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase } from "bun:sqlite";

import { type SqliteColumnDef, type SqliteColumnType, type SqliteTableDef } from "../schema";
import { runGetTableXInfo } from "./runGetTableXInfo";
import { runMigration } from "./runMigration";

/**
 * Migrates a table by updating a column's properties. This creates a new table with the desired schema,
 * copies all data from the old table, drops the old table, and renames the new table.
 *
 * @param db - The database connection
 * @param tableName - The name of the table to update
 * @param columnName - The name of the column to update
 * @param updatedColumn - The new column definition
 */
export async function runColumnUpdateMigration(
	db: BunDatabase | TauriDatabase,
	tableName: string,
	columnName: string,
	updatedColumn: SqliteColumnDef
): Promise<void> {
	// Get current table info
	const currentTable = await runGetTableXInfo(db, tableName);

	// Find the column to update
	const columnIndex = currentTable.columns.findIndex((col) => col.name === columnName);
	if (columnIndex === -1) {
		throw new Error(`Column ${columnName} not found in table ${tableName}`);
	}

	// Create desired table definition with updated column
	const desiredTable: SqliteTableDef = {
		tableName,
		columns: currentTable.columns.map((col, index) => {
			if (index === columnIndex) {
				// Use the updated column definition
				return updatedColumn;
			}
			// Keep other columns as they are
			return {
				name: col.name,
				type: col.type as SqliteColumnType,
				nullable: col.notnull === 0,
				primaryKey: col.pk === 1,
				unique: col.pk === 1,
			};
		}),
	};

	// If we're renaming a column, pass both old and new names to runMigration
	const isRenaming = columnName !== updatedColumn.name;
	await runMigration(
		db,
		desiredTable,
		isRenaming ? { oldColumnName: columnName, newColumnName: updatedColumn.name } : undefined
	);
}
